import React,{ forwardRef } from "react"
import { cva } from "class-variance-authority"
import { X } from 'lucide-react'
import { cn } from "../../lib/utils"
import PropTypes from "prop-types";

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-md border p-4 pr-6 shadow-lg transition-all",
  {
    variants: {
      variant: {
        default: "bg-neutral-1 text-neutral-10 border-neutral-10",
        success: "bg-system-positive-1 text-system-positive-2 border-system-positive-2",
        warning: "bg-system-warning-1 text-system-warning-2 border-system-warning-2",
        error: "bg-system-negative-1 text-system-negative-2 border-system-negative-2", 
        info: "bg-system-info-1 text-system-info-2 border-system-info-2",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Toast = forwardRef(
  ({ className, variant, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(toastVariants({ variant }), "animate-slide-in opacity-100 transition-all duration-300 ease-in-out [&.animate-slide-out]:opacity-0 [&.animate-slide-out]:translate-y-[-20px]", className)}
        {...props}
      />
    )
  }
)
Toast.displayName = "Toast"
Toast.prototype = {
  className: PropTypes.string,
  variant: PropTypes.oneOf(["default", "success", "warning", "error", "info"]),
  children: PropTypes.node,
};

const ToastAction = forwardRef(
  ({ className, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        "inline-flex h-8 shrink-0 items-center justify-center rounded-md bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50",
        className
      )}
      {...props}
    />
  )
)
ToastAction.displayName = "ToastAction"
ToastAction.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  children: PropTypes.node,
};

const ToastClose = forwardRef(
  ({ className, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        "absolute right-1 top-1 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100",
        className
      )}
      toast-close=""
      {...props}
    >
      <X className="h-4 w-4" />
    </button>
  )
)
ToastClose.displayName = "ToastClose"
ToastClose.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
};

const ToastTitle = forwardRef(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("text-sm font-semibold [&+div]:text-xs", className)}
      {...props}
    />
  )
)
ToastTitle.displayName = "ToastTitle"
// @ts-ignore
ToastTitle.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const ToastDescription = forwardRef(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("text-sm opacity-90", className)}
      {...props}
    />
  )
)
ToastDescription.displayName = "ToastDescription"
ToastDescription.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

export {
  Toast,
  ToastAction,
  ToastClose,
  ToastTitle,
  ToastDescription,
}